import { Controller } from "@hotwired/stimulus"
import _ from "lodash"
import { mapRetrieveToFields } from "../helpers/mapbox_place_search"
import { mapboxToken } from "../helpers/mapbox_token"

const SUGGEST_URL  = "https://api.mapbox.com/search/searchbox/v1/suggest"
const RETRIEVE_URL = "https://api.mapbox.com/search/searchbox/v1/retrieve"
const MIN_QUERY_LENGTH = 3
const DEBOUNCE_MS = 250
const SUGGESTION_LIMIT = 5

export default class extends Controller {
  static targets = [
    "input", "results", "error",
    "name", "address", "locality", "region",
    "postalCode", "country", "latitude", "longitude"
  ]

  static values = {
    country: { type: String, default: "us" }
  }

  initialize() {
    this.sessionToken = generateUuid()
    this.search = _.debounce(this.search.bind(this), DEBOUNCE_MS)
  }

  disconnect() {
    this.search.cancel()
  }

  async search() {
    const query = (this.inputTarget.value || "").trim()
    this.hideError()

    if (query.length < MIN_QUERY_LENGTH) {
      this.hideResults()
      return
    }

    try {
      const url = `${SUGGEST_URL}?` + new URLSearchParams({
        q: query,
        access_token: mapboxToken(),
        session_token: this.sessionToken,
        country: this.countryValue,
        limit: String(SUGGESTION_LIMIT)
      }).toString()

      const response = await fetch(url)
      if (!response.ok) throw new Error(`Mapbox suggest returned ${response.status}`)

      const data = await response.json()
      this.renderSuggestions(data.suggestions || [])
    } catch (e) {
      console.error("Mapbox suggest failed:", e)
      this.showError("Couldn't reach Mapbox — try again")
      this.hideResults()
    }
  }

  renderSuggestions(suggestions) {
    // Clear via removeChild loop, then build nodes with textContent only.
    while (this.resultsTarget.firstChild) {
      this.resultsTarget.removeChild(this.resultsTarget.firstChild)
    }

    if (suggestions.length === 0) {
      const li = document.createElement("li")
      li.className = "list-group-item text-muted"
      li.textContent = "No matches"
      this.resultsTarget.appendChild(li)
    } else {
      suggestions.forEach((s) => {
        const li = document.createElement("li")
        li.className = "list-group-item list-group-item-action"
        li.style.cursor = "pointer"
        li.dataset.mapboxId = s.mapbox_id
        li.dataset.action = "click->mapbox-place-search#select"

        const nameDiv = document.createElement("div")
        nameDiv.className = "fw-semibold"
        nameDiv.textContent = s.name || ""
        li.appendChild(nameDiv)

        const formattedSmall = document.createElement("small")
        formattedSmall.className = "text-muted"
        formattedSmall.textContent = s.place_formatted || ""
        li.appendChild(formattedSmall)

        this.resultsTarget.appendChild(li)
      })
    }

    this.resultsTarget.classList.remove("d-none")
  }

  async select(event) {
    const li = event.currentTarget
    const mapboxId = li.dataset.mapboxId
    if (!mapboxId) return

    this.hideError()

    try {
      const url = `${RETRIEVE_URL}/${encodeURIComponent(mapboxId)}?` + new URLSearchParams({
        access_token: mapboxToken(),
        session_token: this.sessionToken
      }).toString()

      const response = await fetch(url)
      if (!response.ok) throw new Error(`Mapbox retrieve returned ${response.status}`)

      const data = await response.json()
      const feature = (data.features || [])[0]
      this.applyFields(mapRetrieveToFields(feature))

      this.sessionToken = generateUuid()
      this.hideResults()
    } catch (e) {
      console.error("Mapbox retrieve failed:", e)
      this.showError("Couldn't fetch the selected place — try again")
    }
  }

  applyFields(fields) {
    Object.entries(fields).forEach(([key, value]) => {
      if (value === null || value === undefined) return
      const target = this.targetFor(key)
      if (!target) return
      if (key === "name" && target.value && target.value.trim() !== "") return
      target.value = value
      target.dispatchEvent(new Event("input", { bubbles: true }))
      target.dispatchEvent(new Event("change", { bubbles: true }))
    })
  }

  targetFor(key) {
    const map = {
      name: () => this.hasNameTarget && this.nameTarget,
      address: () => this.hasAddressTarget && this.addressTarget,
      locality: () => this.hasLocalityTarget && this.localityTarget,
      region: () => this.hasRegionTarget && this.regionTarget,
      postalCode: () => this.hasPostalCodeTarget && this.postalCodeTarget,
      country: () => this.hasCountryTarget && this.countryTarget,
      latitude: () => this.hasLatitudeTarget && this.latitudeTarget,
      longitude: () => this.hasLongitudeTarget && this.longitudeTarget
    }
    const getter = map[key]
    return getter ? (getter() || null) : null
  }

  clear() {
    // Delay so a click on a suggestion <li> still fires before we hide it.
    setTimeout(() => this.hideResults(), 150)
  }

  hideResults() {
    while (this.resultsTarget.firstChild) {
      this.resultsTarget.removeChild(this.resultsTarget.firstChild)
    }
    this.resultsTarget.classList.add("d-none")
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove("d-none")
  }

  hideError() {
    this.errorTarget.textContent = ""
    this.errorTarget.classList.add("d-none")
  }
}

function generateUuid() {
  if (window.crypto && typeof window.crypto.randomUUID === "function") {
    return window.crypto.randomUUID()
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === "x" ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}
